import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import OneSignal from '@onesignal/capacitor-plugin';
import { resolveInAppPath } from 'common/utils/appLinks';
import { queueNavigation } from 'common/utils/pushRegistration';
import { trackEvent, MixpanelEventName } from 'common/utils/mixpanel/eventTriggers';

let clickListenerAttached = false;

// A tap on a OneSignal push (or in-app message) routes through the same
// allowlist and auth-gated queue as every other link. Kept out of
// onesignal.ts so that file does not import eventTriggers (which imports it).
// Call once at boot, right after initOneSignal, so cold-start taps are caught.
export const attachOneSignalClickListener = (): void => {
  if (!Capacitor.isNativePlatform() || clickListenerAttached) return;
  clickListenerAttached = true;
  try {
    OneSignal.Notifications.addEventListener('click', (event) => {
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const n: any = event?.notification ?? {};
        const data = n.additionalData ?? {};
        const candidates = [data.route, data.link, n.launchURL]
          .filter((c): c is string => typeof c === 'string' && !!c);
        // First candidate that is one of our in-app routes wins. Launch URLs
        // are suppressed natively (Info.plist and AndroidManifest), so a web
        // link that is not ours opens in the in-app browser instead.
        let path: string | null = null;
        for (let i = 0; i < candidates.length && !path; i += 1) path = resolveInAppPath(candidates[i]);
        if (path) {
          queueNavigation(path);
        } else {
          const external = candidates.find((c) => c.startsWith('https://'));
          if (external) Browser.open({ url: external }).catch(() => undefined);
        }
        trackEvent(MixpanelEventName.pushNotificationOpened, {
          source: 'onesignal',
          route: path ?? 'none',
          platform: Capacitor.getPlatform(),
        });
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('[onesignal] click handling failed', e);
      }
    });
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[onesignal] click listener failed', e);
  }
};
